import { memo } from "react";
import { useNavigate } from "react-router-dom";
import { Users, UserCircle, Settings, User, LogOut } from "lucide-react";
import { motion, AnimatePresence } from "framer-motion";
import {
  Drawer,
  DrawerContent,
  DrawerTrigger,
  DrawerHandle,
} from "@/components/ui/drawer";
import { InstantAvatar } from "@/components/ui/instant-avatar";
import { StableAvatar } from "@/components/ui/stable-avatar";
import { useNativeGestures } from "@/hooks/use-native-gestures";

interface MobileProfileSheetProps {
  isOpen: boolean;
  onOpenChange: (open: boolean) => void;
  avatarUrl: string | null;
  displayName: string;
  email: string;
  isAdmin: boolean;
  isKidsProfile: boolean;
  subscriptionStatus?: {
    subscription_status?: string;
  } | null;
  isAvatarRefreshing: boolean;
  navProfileRef: React.RefObject<HTMLDivElement>;
  onSwitchProfiles: () => void;
  onLogout: () => void;
  isCompact?: boolean;
}

const menuItems = [
  { id: 'profiles', icon: Users, label: 'Switch Profiles', desc: 'Change your reading profile', path: null },
  { id: 'profile', icon: UserCircle, label: 'Profile', desc: 'Manage your account', path: '/profile' },
  { id: 'settings', icon: Settings, label: 'Settings', desc: 'Preferences and options', path: '/settings' },
  { id: 'admin', icon: User, label: 'Admin', desc: 'Dashboard and controls', path: '/admin' },
];

export const MobileProfileSheet = memo(function MobileProfileSheet({
  isOpen,
  onOpenChange,
  avatarUrl,
  displayName,
  email,
  isAdmin,
  isKidsProfile,
  subscriptionStatus,
  isAvatarRefreshing,
  navProfileRef,
  onSwitchProfiles,
  onLogout,
  isCompact = false,
}: MobileProfileSheetProps) {
  const navigate = useNavigate();
  const { triggerLight, triggerMedium } = useNativeGestures({ hapticFeedback: true });

  const handleItemClick = (item: typeof menuItems[0]) => {
    triggerLight();
    
    if (item.id === 'profiles') {
      onSwitchProfiles();
    } else if (item.path) {
      navigate(item.path);
      window.scrollTo(0, 0);
    }
    
    onOpenChange(false);
  };

  const handleLogout = () => {
    triggerMedium();
    onLogout();
    onOpenChange(false);
  };

  const filteredItems = menuItems.filter(item => {
    if (item.id === 'admin' && !isAdmin) return false;
    if ((item.id === 'profile' || item.id === 'settings') && isKidsProfile) return false;
    return true;
  });

  const getSubscriptionBadge = () => {
    if (subscriptionStatus?.subscription_status === 'active') {
      return { text: 'Active Subscription', color: 'text-green-400', icon: '✓' };
    }
    if (subscriptionStatus?.subscription_status === 'cancelled') {
      return { text: 'Subscription Ending', color: 'text-orange-400', icon: '⏰' };
    }
    return { text: 'No Subscription', color: 'text-white/50', icon: null };
  };

  const badge = getSubscriptionBadge();

  return (
    <Drawer open={isOpen} onOpenChange={onOpenChange} handleOnly>
      <DrawerTrigger asChild>
        <button 
          className="flex items-center justify-center rounded-full focus:outline-none focus-visible:outline-none active:scale-95 transition-transform duration-150"
          onClick={() => triggerLight()}
        >
          <div 
            ref={navProfileRef}
            data-nav-avatar="true"
            className={isAvatarRefreshing ? 'animate-spin' : ''}
            style={{ 
              animationDuration: isAvatarRefreshing ? '0.6s' : undefined,
              animationTimingFunction: 'ease-in-out'
            }}
          >
            <InstantAvatar 
              avatarUrl={avatarUrl} 
              fallbackText={displayName} 
              size="md" 
              fallback="none"
              className={`border border-white/20 active:border-white/40 transition-all duration-300 ${isCompact ? 'h-6 w-6' : 'h-7 w-7'}`} 
            />
          </div>
        </button>
      </DrawerTrigger>
      
      <DrawerContent className="bg-black/95 backdrop-blur-2xl border-t border-white/10 pb-safe focus:outline-none">
        {/* Drag handle (the only draggable area) */}
        <div className="flex justify-center pt-3 pb-2" data-vaul-no-drag>
          <DrawerHandle className="w-10 h-1 rounded-full bg-white/30" />
        </div>
        
        {/* Profile Header */}
        <div className="px-6 pb-5">
          <motion.div 
            className="flex items-center gap-4"
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.05 }}
          >
            <StableAvatar 
              avatarUrl={avatarUrl} 
              fallbackText={displayName} 
              size="lg" 
              className="h-16 w-16 border-2 border-white/20 shadow-xl" 
            />
            <div className="flex-1 min-w-0">
              <p className="text-xl font-semibold text-white truncate">{displayName}</p>
              <p className="text-sm text-white/50 truncate">{email}</p>
              <div className="flex items-center gap-1.5 mt-1">
                {badge.icon && <span className="text-xs">{badge.icon}</span>}
                <span className={`text-xs font-medium ${badge.color}`}>{badge.text}</span>
              </div>
            </div>
          </motion.div>
        </div>

        {/* Divider */}
        <div className="h-px bg-white/10 mx-6" />

        {/* Menu Items */}
        <div className="px-4 py-3 space-y-1" data-vaul-no-drag>
          <AnimatePresence>
            {filteredItems.map((item, index) => {
              const Icon = item.icon;
              return (
                <motion.button
                  key={item.id}
                  data-vaul-no-drag
                  initial={{ opacity: 0, x: -20 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{ delay: 0.08 + index * 0.04 }}
                  onClick={() => handleItemClick(item)}
                  className="flex items-center gap-4 w-full p-4 rounded-2xl bg-white/5 active:bg-white/10 transition-colors touch-manipulation"
                >
                  <div className="flex items-center justify-center w-10 h-10 rounded-xl bg-white/10">
                    <Icon size={20} className="text-white" />
                  </div>
                  <div className="flex-1 text-left">
                    <p className="text-base font-medium text-white">{item.label}</p>
                    <p className="text-sm text-white/50">{item.desc}</p>
                  </div>
                </motion.button>
              );
            })}
          </AnimatePresence>
        </div>

        {/* Divider */}
        <div className="h-px bg-white/10 mx-6" />

        {/* Logout */}
        <div className="px-4 py-3 pb-6" data-vaul-no-drag>
          <motion.button
            data-vaul-no-drag
            initial={{ opacity: 0, x: -20 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ delay: 0.2 }}
            onClick={handleLogout}
            className="flex items-center gap-4 w-full p-4 rounded-2xl bg-red-500/10 active:bg-red-500/20 transition-colors touch-manipulation"
          >
            <div className="flex items-center justify-center w-10 h-10 rounded-xl bg-red-500/20">
              <LogOut size={20} className="text-red-400" />
            </div>
            <div className="flex-1 text-left">
              <p className="text-base font-medium text-red-400">Log Out</p>
              <p className="text-sm text-white/40">Sign out of your account</p>
            </div>
          </motion.button>
        </div>
      </DrawerContent>
    </Drawer>
  );
});
